/* eslint-disable @typescript-eslint/no-explicit-any */
import { format, subYears } from "date-fns";
import React, { useMemo } from "react";
import { EventRegister } from "react-native-event-listeners";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import { useProgramData } from "@hooks/data/useProgramData";
import {
  AllProgressCheckStatuses,
  CompetenceActivityClass,
  EvidenceCustomTag,
  IEvidence,
  IUser,
  ProgramSkill,
  ProgressCheck,
} from "@interfaces/Interfaces";
import { evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import { IProgramEvidenceCard } from "@interfaces/Components";
import * as ProgramUtils from "@utils/programUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import EvidenceCard from "@components/evidence/EvidenceCard";
import classNames from "classnames";
import { Icon_Audit_Plus, Icon_Audit_Tick } from "@assets/images";
import { DOT_SEPARATOR } from "@utils/stringUtils";
import { userRolesSelector } from "@state/UserState";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import { useToggleAuditTag } from "@hooks/evidence/useToggleAuditTag";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";

const ProgramEvidenceCard: React.FC<IProgramEvidenceCard> = (props) => {
  const { userPrograms } = useProgramData();
  const { optimisticAuditTag, toggleAuditTag } = useToggleAuditTag(props.programEvidence);
  const { currentProgramCycle } = useProgramCycles(props.program?.ID);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const allEvidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allUserRoles = useRecoilValue(userRolesSelector);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[]>(progressCheckDataAtom);

  const evidenceJSON = props.programEvidence.evidenceJSON;
  const title = evidenceJSON?.Title ?? props.programEvidence.title ?? "";

  const activityTypeKey = ProgramUtils.getEvidenceActivityTypeKey(props.programEvidence);

  const skill = EvidenceUtils.getEvidenceSkillKey(props.programEvidence);
  const skillType = EvidenceUtils.getEvidenceSkillTypeKey(props.programEvidence);

  const skillsSelectLabel = EvidenceUtils.getEvidenceSkillsSelectLabel(props.programEvidence);

  const activityType = activityTypeKey && evidenceJSON?.[activityTypeKey];
  const taskingType = evidenceJSON?.["Tasking type or training"];

  const customTagsKey = ProgramUtils.getEvidenceCustomTagsKey(props.programEvidence);
  const customTags: EvidenceCustomTag[] = customTagsKey && evidenceJSON?.[customTagsKey];

  const progressCheck: ProgressCheck | null = useMemo(
    () =>
      evidenceJSON?.programInfo?.progressCheckID &&
      ProgramUtils.getProgressCheckForEvidence(evidenceJSON, props.program),
    [evidenceJSON, props.program]
  );

  const progressCheckName = progressCheck?.Name ?? evidenceJSON?.programInfo?.progressCheck ?? null;

  const progressCheckCompetence: CompetenceActivityClass | null = useMemo(
    () =>
      evidenceJSON?.programInfo?.progressCheckCompetenceID &&
      ProgramUtils.getProgressCheckCompetenceActivityForEvidence(evidenceJSON, props.program),
    [evidenceJSON, props.program]
  );

  const programEvidenceType = evidenceJSON
    ? ProgramUtils.getEvidenceType({
        evidenceJSON: evidenceJSON,
        usesAdditionalEvidenceDefinitions: props.program?.UsesAdditionalEvidenceDefinitions ?? false,
        evidenceDefinitions: props.program?.["Evidence Definitions"] ?? [],
        formEvidenceDefinitions:
          props.program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [],
      })
    : null;

  const evidenceTypeString = programEvidenceType
    ? ProgramUtils.getEvidenceTypeString(props.program, programEvidenceType)
    : null;

  const progressCheckCompetenceName =
    progressCheckCompetence?.Name ?? evidenceJSON?.programInfo?.progressCheckCompetence ?? null;

  const onHoldReset = evidenceJSON?.OnHoldReset === 1;

  const attachmentsLength = EvidenceUtils.getProgramEvidenceAttachmentsLength(props.programEvidence);

  const witnessSimulationSkill = EvidenceUtils.getWitnessSimulationSkill(props.programEvidence, props.program);
  const skillSection = EvidenceUtils.getSkillSectionForEvidence(props.programEvidence, props.program);

  const witnessEvidenceApproved = evidenceJSON.programInfo.witnessSimulationApproval;
  const pedName = ProgramUtils.getUOCPEdName(props.programEvidence);

  const getLabel = () => {
    if (props.program && ProgramUtils.showStandardsLabelForEvidenceCard(props.program.ID)) {
      try {
        const standards: string[] = [];
        const parsedEvidence = props.programEvidence.evidenceJSON;
        const comps: any[] = Array.from(parsedEvidence.Comps);
        comps.forEach((comp) => standards.push(comp.Name));
        return standards.join(", ");
      } catch (error) {
        return "";
      }
    } else {
      return "";
    }
  };

  const label = getLabel();

  let expired;
  let pending;

  if (ProgramUtils.isEPRR(props.program?.ID)) {
    expired = new Date(props.programEvidence.date) < subYears(new Date(), 2);
  }

  if (EvidenceUtils.isWPBAFormEvidenceType(programEvidenceType)) {
    expired = SPAPRPUtils.isWPBAFormEvidenceExpired(props.programEvidence);
    pending = SPAPRPUtils.isWPBAFormEvidencePending(props.programEvidence);
  } else {
    const approvalPending =
      evidenceJSON?.programInfo?.approvalState === "Pending" || evidenceJSON?.programInfo?.pending === true;
    if (approvalPending) {
      const expiryDate = evidenceJSON?.programInfo?.expiryDate;
      if (expiryDate && new Date(expiryDate) < new Date()) {
        expired = true;
      } else {
        pending = pending || true;
      }
    }
  }

  const approvalPending = evidenceJSON?.programInfo?.approvalState === "Pending";
  if (approvalPending) {
    const expiryDate = evidenceJSON?.programInfo?.expiryDate;
    if (expiryDate && new Date(expiryDate) < new Date()) {
      expired = true;
    } else {
      pending = pending || true;
    }
  }

  const editEvidence = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    const parsedEvidence = props.programEvidence.evidenceJSON;

    const program = userPrograms.find((program) => program.ID === parsedEvidence.programInfo.programID);
    const pc = program?.ProgressChecks?.find(
      (progressCheck) => progressCheck.ID === parsedEvidence.programInfo.progressCheckID
    );

    let competences: CompetenceActivityClass[] = [];
    let progressCheckCompetences: CompetenceActivityClass[] = [];
    let progressCheckCompetence: any;
    let mandatoryStandards: CompetenceActivityClass[] = [];
    let optionalStandards: CompetenceActivityClass[] = [];
    let skills: ProgramSkill[] = [];

    if (program) {
      if (ProgramUtils.shouldGetProgressCheckInfo(program.ID)) {
        progressCheckCompetences = pc?.["Competence/Activity"] ?? [];
        progressCheckCompetence = progressCheckCompetences.find(
          (item: any): item is CompetenceActivityClass =>
            item.ID === parsedEvidence.programInfo?.progressCheckCompetenceID
        );
      } else {
        competences = program.CompetenceActivity ?? [];
      }

      if (user.state === "hasValue" && user.contents) {
        mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, program!);
        optionalStandards = ProgramUtils.getOptionalStandards(user.contents, program!);
      }

      skills = ProgramUtils.getSkillsForRole(user.contents, program!);
    }

    const competence = competences.find(
      (item: any): item is CompetenceActivityClass => item.Name === parsedEvidence.programInfo?.competence
    );

    const evidenceDefinitions = program?.["Evidence Definitions"] || [];
    const formEvidenceDefinitions =
      props.program?.["WPBA Forms"]?.flatMap((form) => form["Evidence Definitions"] ?? []) ?? [];
    const progressChecks: any = program?.ProgressChecks ?? [];

    const usesAdditionalEvidenceDefinitions = program?.UsesAdditionalEvidenceDefinitions;

    let data: object = {};

    const evidenceType = ProgramUtils.getEvidenceType({
      evidenceJSON: parsedEvidence,
      usesAdditionalEvidenceDefinitions: usesAdditionalEvidenceDefinitions ?? false,
      evidenceDefinitions,
      formEvidenceDefinitions,
    });

    if (evidenceType === "skills") {
      data = {
        program: program,
        ...(competences.length > 0 && { competences }),
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinitions[0],
          }),
        competence: competence,
        evidence: props.programEvidence,
        ...(progressCheckCompetence && {
          progressCompetence: progressCheckCompetence,
        }),
        progressCheck: pc,
        ...(progressChecks.length > 0 && !usesAdditionalEvidenceDefinitions && { progressChecks }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        evidenceJSON: props.programEvidence.evidenceJSON,
        skill: null,
        id: props.programEvidence.id,
        learningOutcomes: progressCheckCompetence?.["Learning Objective"] ?? [],
        progressCheckCompetence: progressCheckCompetence,
        currentProgressCheckId: pc?.ID ?? "",
        disableProgressCheckDropdown: pc !== undefined,
        disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
      };
    } else if (evidenceType === "witness-simulation") {
      const evidenceDefinition = evidenceDefinitions.find(
        (item) => item.EvidenceSelectionType === "witness-simulation"
      );
      const preselectedSkillID = parsedEvidence.programInfo?.witnessSimulationSkillID;
      const preselectedSkill = skills.find((item) => item.ID === preselectedSkillID);

      data = {
        ...data,
        program: program,
        evidenceDefinitions: evidenceDefinition,
        evidence: props.programEvidence,
        ...(skills.length > 0 && { skills }),
        ...(preselectedSkill && { preselectedSkill }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
      };
    } else if (evidenceType === "capabilities") {
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
      let userRoles: string[] = [];

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "capabilities");
      const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

      data = {
        ...data,
        program: program,
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
      };
    } else if (evidenceType === "medical-imaging") {
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
      let userRoles: string[] = [];

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "medical-imaging");
      const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];

      data = {
        ...data,
        program: program,
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        ...(skills.length > 0 && { skills }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
      };
    } else if (evidenceType === "wpba-supervision-form") {
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
      let userRoles: string[] = [];

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];
      const evidenceDefinition = formEvidenceDefinitions.find(
        (item) => item.ID === parsedEvidence.programInfo.programEvidenceDefinitionID
      );

      const wpbaForm = props.program?.["WPBA Forms"]?.find((form) =>
        form["Evidence Definitions"]?.find((item) => item.ID === parsedEvidence.programInfo.programEvidenceDefinitionID)
      );

      data = {
        ...data,
        program: program,
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        ...(skills.length > 0 && { skills }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
        wpbaForm: wpbaForm,
      };
    } else if (evidenceType === "ropp-workbook") {
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
      let userRoles: string[] = [];

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "ropp-workbook");
      const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];
      const skillID = parsedEvidence.programInfo?.skillID;
      const autoFilledSkill = skillID ? (program?.Skills?.find((s) => s.ID === skillID) ?? null) : null;

      data = {
        ...data,
        program: program,
        ...(evidenceDefinition && { evidenceDefinitions: evidenceDefinition }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        ...(autoFilledSkill && { autoFilledSkill }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
      };
    } else if (evidenceType === "cap-workbook") {
      const programRoles = allUserRoles.find((role) => role.programID === program?.ID);
      let userRoles: string[] = [];

      if (programRoles) {
        userRoles = ProgramUtils.mapProgramRolesToRoleNames(programRoles.programRoles);
      }

      const evidenceDefinition = evidenceDefinitions.find((item) => item.EvidenceSelectionType === "cap-workbook");
      const learningObjectives = program ? ProgramUtils.getAllLearningObjectivesForRole(program, userRoles[0]) : [];
      const skillID = parsedEvidence.programInfo?.skillID;
      const autoFilledSkill = skillID ? (program?.Skills?.find((s) => s.ID === skillID) ?? null) : null;

      data = {
        ...data,
        program: program,
        ...(evidenceDefinition && { evidenceDefinitions: evidenceDefinition }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(learningObjectives.length > 0 && { learningObjectives }),
        ...(autoFilledSkill && { autoFilledSkill }),
        evidenceJSON: parsedEvidence,
        id: props.programEvidence.id,
      };
    } else {
      const _evidenceDefinition = usesAdditionalEvidenceDefinitions
        ? evidenceDefinitions.find((d) => d.ID === parsedEvidence.programInfo?.programEvidenceDefinitionID)
        : evidenceDefinitions[0];

      const skillID = parsedEvidence.programInfo?.skillID;
      const _autoFilledSkill = skillID ? skills.find((s) => s.ID === skillID) ?? null : null;
      const existingApprovalState = parsedEvidence.programInfo?.approvalState;

      data = {
        program: program,
        ...(competences.length > 0 && { competences }),
        ...(_evidenceDefinition && { evidenceDefinitions: _evidenceDefinition }),
        competence: competence,
        evidence: props.programEvidence,
        ...(progressCheckCompetence && {
          progressCompetence: progressCheckCompetence,
        }),
        progressCheck: pc,
        ...(progressChecks.length > 0 && { progressChecks }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        evidenceJSON: props.programEvidence.evidenceJSON,
        skill: null,
        id: props.programEvidence.id,
        learningOutcomes: progressCheckCompetence?.["Learning Objective"] ?? [],
        progressCheckCompetence: progressCheckCompetence,
        currentProgressCheckId: pc?.ID ?? "",
        disableProgressCheckDropdown: pc !== undefined,
        disableProgressCheckCompetenceDropdown: progressCheckCompetence !== undefined,
        ...(_autoFilledSkill && { autoFilledSkill: _autoFilledSkill }),
        ...(existingApprovalState && { approvalState: existingApprovalState }),
      };
    }

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const canEdit = EvidenceUtils.canEdit(
    props.programEvidence,
    userPrograms,
    allProgressCheckData,
    props.progressCheckLocked,
    allEvidence.contents,
    currentProgramCycle
  );

  return (
    <div>
      <div className="flex justify-center">
        <EvidenceCard
          onClick={() => props.buttonPressed(props.programEvidence)}
          style={{
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 12,
            paddingBottom: 12,
          }}
        >
          <div className="flex w-full flex-col !px-0 py-3">
            <div className="flex flex-row flex-nowrap items-center">
              <span className="flex w-full flex-row flex-wrap items-center gap-2">
                {props.programEvidence.evidenceJSON.programInfo.programCycle && (
                  <span
                    className={classNames("flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2", {
                      "!bg-green-background":
                        props.programEvidence.evidenceJSON.programInfo.programCycle === currentProgramCycle,
                    })}
                  >
                    <span
                      className={classNames("text-12px font-semibold text-grey-60", {
                        "!text-green-text":
                          props.programEvidence.evidenceJSON.programInfo.programCycle === currentProgramCycle,
                      })}
                    >{`Cycle ${props.programEvidence.evidenceJSON.programInfo.programCycle}`}</span>
                  </span>
                )}
                {expired && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-cta-red px-2">
                    <span className="text-12px font-semibold text-white">{"Expired"}</span>
                  </span>
                )}
                {pending && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-orange-30 px-2">
                    <span className="text-12px font-semibold text-orange-text">{"Pending"}</span>
                  </span>
                )}
                {evidenceJSON?.programInfo?.approvalState === "Approved" && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                    <span className="text-12px font-semibold text-green-text">{"Approved"}</span>
                  </span>
                )}
                {evidenceJSON?.programInfo?.approvalState === "Rejected" && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-cta-red px-2">
                    <span className="text-12px font-semibold text-white">{"Rejected"}</span>
                  </span>
                )}
                {props.programEvidence.private && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2">
                    <span className="text-12px font-semibold text-grey-60">{"Private"}</span>
                  </span>
                )}
                {witnessEvidenceApproved && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                    <span className="text-12px font-semibold text-green-text">{"Approved"}</span>
                  </span>
                )}
                {props.programEvidence.evidenceJSON.programInfo.locked && (
                  <>
                    {EvidenceUtils.isWPBAFormEvidenceType(programEvidenceType) ? (
                      <span className="flex h-4 items-center justify-center rounded-xl bg-green-background px-2">
                        <span className="text-12px font-semibold text-green-text">{"Completed"}</span>
                      </span>
                    ) : (
                      <span className="flex h-4 items-center justify-center rounded-xl bg-grey-30 px-2">
                        <span className="text-12px font-semibold text-grey-60">{"Locked"}</span>
                      </span>
                    )}
                  </>
                )}
                {props.programEvidence.draft && (
                  <span className="flex h-4 items-center justify-center rounded-xl bg-orange-30 px-2">
                    <span className="text-12px font-semibold text-orange-text">{"Draft"}</span>
                  </span>
                )}
                <span className="flex flex-row flex-wrap items-center">
                  <span className="text-acc-12px font-acc-500 leading-[1.33] tracking-[-0.1px] text-grey-70">
                    {format(new Date(props.programEvidence.date), "d MMM yyyy")}
                  </span>
                  {activityType && (
                    <span className="evidenceCardProgramActivity">
                      {DOT_SEPARATOR}
                      {activityType}
                    </span>
                  )}
                  {progressCheckName && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {progressCheckName}
                    </span>
                  )}
                  {progressCheckCompetenceName && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {progressCheckCompetenceName}
                    </span>
                  )}
                  {evidenceTypeString && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {evidenceTypeString}
                    </span>
                  )}
                  {skill && (
                    <div className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {skill}
                    </div>
                  )}
                  {skillType && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {skillType}
                    </span>
                  )}
                  {witnessSimulationSkill && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {witnessSimulationSkill.Name}
                    </span>
                  )}
                  {skillSection && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {skillSection}
                    </span>
                  )}
                  {skillsSelectLabel && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {skillsSelectLabel}
                    </span>
                  )}
                  {onHoldReset && (
                    <span className="whitespace-break-spaces text-acc-12px font-acc-500 leading-[1.33] tracking-[-0.1px] text-cta-red">
                      {DOT_SEPARATOR}
                      {"Submission Reset"}
                    </span>
                  )}
                  {attachmentsLength > 0 && (
                    <span className="text-acc-12px font-acc-600 leading-[1.33] tracking-[-0.1px] text-portfolio-blue">
                      {DOT_SEPARATOR}
                      {`${attachmentsLength} attachment${attachmentsLength > 1 ? "s" : ""}`}
                    </span>
                  )}
                  {pedName && (
                    <span className="leading-acc-lh text-acc-12px font-acc-600 text-program-evidence-header">
                      {DOT_SEPARATOR}
                      {`PEd: ${pedName}`}
                    </span>
                  )}
                </span>
              </span>
            </div>
            <div className="flex items-center">
              <div
                className={classNames(
                  `acc-underline leading-acc-lh mb-2 mt-[4px] flex-1 text-acc-20px font-semibold tracking-[-0.1px] text-grey-90 max-[500px]:text-acc-17px max-[500px]:leading-default`,
                  {
                    "mr-4": canEdit,
                  }
                )}
              >
                {title}
              </div>
              {canEdit && (
                <button
                  onClick={(e) => editEvidence(e)}
                  className="self-center rounded-[4px] bg-grey-20 px-1.5 py-1 text-acc-14px font-semibold text-cta-blue"
                >
                  {"Edit"}
                </button>
              )}
            </div>
            <span
              className={classNames(
                "acc-underline overflow-visible text-acc-12px font-acc-500 leading-default tracking-[-0.1px] text-grey-70",
                {
                  "mb-2": taskingType || (customTagsKey && customTags.length > 0) || (label && label.length > 0),
                }
              )}
            >
              {taskingType && <div>{taskingType}</div>}
              {customTags && customTags.length > 0 && (
                <span className="mb-2 overflow-visible text-acc-12px font-acc-500 leading-default tracking-[-0.1px] text-grey-70">
                  {customTags.map((tag: EvidenceCustomTag) => tag.tag).join(", ")}
                </span>
              )}
              {label && label.length > 0 && (
                <span className="mb-2 overflow-visible text-acc-12px font-acc-500 leading-default tracking-[-0.1px] text-grey-60">
                  {label}
                </span>
              )}
            </span>
            {EvidenceUtils.hideAuditTag(props.program?.ID ?? "") ? null : (
              <button
                onClick={(e) => toggleAuditTag(e)}
                className={classNames(
                  "self-start rounded-[4px] bg-grey-20 px-1.5 py-1 text-acc-14px font-semibold text-cta-blue",
                  {
                    "!bg-green-200 text-neutral-800": optimisticAuditTag,
                  }
                )}
              >
                {optimisticAuditTag ? (
                  <div className="flex flex-row items-center justify-center">
                    <img src={Icon_Audit_Tick} />
                    <div className="ml-1">{"Added to audit"}</div>
                  </div>
                ) : (
                  <div className="flex flex-row items-center justify-center">
                    <img
                      src={Icon_Audit_Plus}
                      className="mr-2"
                    />
                    <div>{"Add to audit"}</div>
                  </div>
                )}
              </button>
            )}
          </div>
        </EvidenceCard>
      </div>
    </div>
  );
};

export default ProgramEvidenceCard;
